/**
 * Projects route — Git Dashboard.
 * Full repo overview: status, branches, commits, file tree.
 * Covers both worktrees (/root/Projects) and bare repos (/var/git-repos).
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  GitBranch, ChevronDown, ChevronRight, History, Archive,
  FolderGit2, Loader2, Download, Upload, Code2, Folder, FileCode,
  GitCommitHorizontal, RefreshCw, AlertCircle, CheckCircle, PackageOpen,
} from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { StatusDot } from "../components/ui/StatusDot";
import { HexBadge } from "../components/ui/HexBadge";
import { GlyphButton } from "../components/ui/GlyphButton";

interface GitCommit {
  hash: string;
  fullHash?: string;
  message: string;
  date: string;
  author?: string;
}

interface BranchInfo {
  name: string;
  current: boolean;
  hash: string;
  subject: string;
}

interface TreeEntry {
  name: string;
  type: "blob" | "tree";
  hash: string;
}

type ProjectKind = "worktree" | "bare";
type ProjectStatus = "clean" | "dirty" | "ahead" | "behind" | "bare";

interface Project {
  name: string;
  path: string;
  kind: ProjectKind;
  branch: string;
  status: ProjectStatus;
  lastCommit: GitCommit | null;
  hasWorktree: boolean;
  worktreePath: string | null;
}

function statusVariant(s: ProjectStatus): "online" | "warning" | "offline" {
  if (s === "clean") return "online";
  if (s === "bare") return "offline";
  return "warning";
}

function statusLabel(s: ProjectStatus): string {
  const map: Record<ProjectStatus, string> = {
    clean: "CLEAN", dirty: "UNCOMMITTED", ahead: "AHEAD", behind: "BEHIND", bare: "BARE",
  };
  return map[s] ?? "UNKNOWN";
}

function formatDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return d; }
}

// ============================================================================
// Project Detail Panel — branches, commits, tree
// ============================================================================

function ProjectDetail({ project, onClose }: { project: Project; onClose: () => void }) {
  const [tab, setTab] = useState<"commits" | "branches" | "tree">("commits");
  const [treePath, setTreePath] = useState("");
  const [gitActionLoading, setGitActionLoading] = useState<string>("");
  const [gitActionError, setGitActionError] = useState<string>("");
  const [zipState, setZipState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [zipUrl, setZipUrl] = useState<string | null>(null);
  const [zipFilename, setZipFilename] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: gitStatus, refetch: refetchGitStatus } = useQuery({
    queryKey: ["project-git-status", project.path],
    queryFn: async () => {
      const res = await fetch("/api/git/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paths: [project.path] }),
      });
      const data = await res.json() as { repos: { ahead: number; behind: number; isDirty: boolean; error?: string | null }[] };
      return data.repos[0] ?? null;
    },
    enabled: project.kind === "worktree",
  });

  const runGitAction = async (action: "fetch" | "pull") => {
    setGitActionLoading(action);
    setGitActionError("");
    try {
      const res = await fetch("/api/git/action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: project.path, action }),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || data.error) {
        setGitActionError(data.error ?? `HTTP ${res.status}`);
        return;
      }
      // Refresh git status + commits after pull
      await refetchGitStatus();
      if (action === "pull") {
        queryClient.invalidateQueries({ queryKey: ["project-commits", project.name] });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
    } catch (err) {
      setGitActionError(err instanceof Error ? err.message : "Network error");
    } finally {
      setGitActionLoading("");
    }
  };

  const { data: commitsData } = useQuery({
    queryKey: ["project-commits", project.name],
    queryFn: () => fetch(`/api/projects/${project.name}/commits?limit=30`).then((r) => r.json()) as Promise<{ commits: GitCommit[] }>,
  });

  const { data: branchesData } = useQuery({
    queryKey: ["project-branches", project.name],
    queryFn: () => fetch(`/api/projects/${project.name}/branches`).then((r) => r.json()) as Promise<{ branches: BranchInfo[] }>,
    enabled: tab === "branches",
  });

  const { data: treeData } = useQuery({
    queryKey: ["project-tree", project.name, treePath],
    queryFn: () =>
      fetch(`/api/projects/${project.name}/tree?path=${encodeURIComponent(treePath)}`)
        .then((r) => r.json()) as Promise<{ entries: TreeEntry[] }>,
    enabled: tab === "tree",
  });

  const commits = commitsData?.commits ?? [];
  const branches = branchesData?.branches ?? [];
  const treeEntries = treeData?.entries ?? [];

  const handleZip = async () => {
    setZipState("loading");
    setZipUrl(null);
    setZipFilename(null);
    setZipError(null);
    try {
      const res = await fetch("/api/share/zip", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: project.path }),
      });
      const data = await res.json() as { url?: string; filename?: string; size?: number; error?: string };
      if (!res.ok || data.error) {
        setZipState("error");
        setZipError(data.error ?? `HTTP ${res.status}`);
        return;
      }
      setZipState("done");
      setZipUrl(data.url ?? null);
      setZipFilename(data.filename ?? null);
    } catch (err) {
      setZipState("error");
      setZipError(err instanceof Error ? err.message : "Network error");
    }
  };

  const tabs: { id: typeof tab; label: string; icon: React.ReactNode; count?: number }[] = [
    { id: "commits", label: "Commits", icon: <GitCommitHorizontal size={13} />, count: commits.length },
    { id: "branches", label: "Branches", icon: <GitBranch size={13} />, count: branches.length },
    { id: "tree", label: "Archivos", icon: <Folder size={13} /> },
  ];

  // Breadcrumb for tree nav
  const pathParts = treePath ? treePath.split("/") : [];
  const breadcrumbs = pathParts.map((part, i) => ({
    label: part,
    path: pathParts.slice(0, i + 1).join("/"),
  }));

  return (
    <CorpoCard accent>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {project.kind === "bare" ? <Archive size={18} className="text-[var(--bw-muted)]" /> : <FolderGit2 size={18} className="text-[var(--bw-accent)]" />}
          <span className="text-lg font-bold uppercase text-[var(--bw-fg)]">{project.name}</span>
          <HexBadge variant="primary">{project.branch}</HexBadge>
          <StatusDot status={statusVariant(project.status)} size="sm" label={statusLabel(project.status)} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {project.kind === "worktree" && (
            <>
              <GlyphButton
                variant="ghost" size="sm"
                icon={gitActionLoading === "fetch" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                onClick={() => runGitAction("fetch")}
                disabled={!!gitActionLoading}
                title="Fetch desde remoto"
              >
                Fetch
              </GlyphButton>
              {(gitStatus?.behind ?? 0) > 0 && (
                <GlyphButton
                  variant="secondary" size="sm"
                  icon={gitActionLoading === "pull" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  onClick={() => runGitAction("pull")}
                  disabled={!!gitActionLoading}
                >
                  Pull ↓{gitStatus!.behind}
                </GlyphButton>
              )}
              {(gitStatus?.ahead ?? 0) > 0 && (
                <span className="text-[10px] font-mono text-[var(--bw-accent)] flex items-center gap-1">
                  <Upload size={10} /> ↑{gitStatus!.ahead}
                </span>
              )}
            </>
          )}
          {project.kind === "worktree" && (
            <GlyphButton variant="secondary" size="sm" icon={<Code2 size={12} />}
              onClick={() => navigate({ to: "/editor" })}
            >
              Editar
            </GlyphButton>
          )}
          <GlyphButton
            variant="ghost" size="sm"
            icon={zipState === "loading" ? <Loader2 size={12} className="animate-spin" /> : <PackageOpen size={12} />}
            onClick={handleZip}
            disabled={zipState === "loading"}
            title="Comprimir proyecto como ZIP"
          >
            ZIP
          </GlyphButton>
          <button onClick={onClose} className="text-xs text-[var(--bw-muted)] hover:text-[var(--bw-fg)] font-mono uppercase">Cerrar</button>
        </div>
      </div>

      {gitActionError && (
        <div className="flex items-center gap-1 text-[10px] font-mono text-[var(--bw-warn,#f77)] mb-2">
          <AlertCircle size={10} /> {gitActionError}
        </div>
      )}

      {zipState === "done" && zipUrl && (
        <div className="flex items-center gap-2 text-[10px] font-mono text-[var(--bw-accent)] mb-2">
          <CheckCircle size={10} />
          <span>ZIP listo:</span>
          <a
            href={zipUrl}
            download={zipFilename ?? undefined}
            className="underline underline-offset-2 hover:opacity-80"
          >
            {zipFilename}
          </a>
          <button
            onClick={() => { setZipState("idle"); setZipUrl(null); }}
            className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)] ml-1"
          >
            ✕
          </button>
        </div>
      )}
      {zipState === "error" && zipError && (
        <div className="flex items-center gap-1 text-[10px] font-mono text-[var(--bw-warn,#f77)] mb-2">
          <AlertCircle size={10} /> ZIP error: {zipError}
        </div>
      )}

      <p className="text-[10px] text-[var(--bw-muted)] font-mono mb-3">{project.path}</p>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--bw-border)] mb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); if (t.id === "tree") setTreePath(""); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border-b-2 transition-colors ${
              tab === t.id
                ? "text-[var(--bw-accent)] border-b-[var(--bw-accent)]"
                : "text-[var(--bw-muted)] border-b-transparent hover:text-[var(--bw-fg)]"
            }`}
          >
            {t.icon} {t.label}
            {t.count !== undefined && <span className="text-[var(--bw-muted)]">({t.count})</span>}
          </button>
        ))}
      </div>

      {/* Commits tab */}
      {tab === "commits" && (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {commits.map((c, i) => (
            <div key={c.hash + i} className="flex items-start gap-2 py-1.5 border-b border-[var(--bw-border)] last:border-0">
              <code className="text-[var(--bw-accent)] text-[11px] font-mono flex-shrink-0 mt-0.5">{c.hash}</code>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[var(--bw-fg)] break-words">{c.message}</p>
                <p className="text-[10px] text-[var(--bw-muted)] mt-0.5">
                  {c.author && <span>{c.author} · </span>}
                  {formatDate(c.date)}
                </p>
              </div>
            </div>
          ))}
          {commits.length === 0 && <p className="text-xs text-[var(--bw-muted)] py-4 text-center">Sin commits</p>}
        </div>
      )}

      {/* Branches tab */}
      {tab === "branches" && (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {branches.map((b) => (
            <div key={b.name} className="flex items-center gap-2 py-1.5 border-b border-[var(--bw-border)] last:border-0">
              <GitBranch size={14} className={b.current ? "text-[var(--bw-accent)]" : "text-[var(--bw-muted)]"} />
              <span className={`text-xs font-mono flex-1 ${b.current ? "text-[var(--bw-accent)] font-bold" : "text-[var(--bw-fg)]"}`}>
                {b.name}
              </span>
              {b.current && <HexBadge variant="primary" size="sm">HEAD</HexBadge>}
              <code className="text-[10px] text-[var(--bw-muted)] font-mono">{b.hash}</code>
            </div>
          ))}
          {branches.length === 0 && <p className="text-xs text-[var(--bw-muted)] py-4 text-center">Sin branches</p>}
        </div>
      )}

      {/* Tree tab */}
      {tab === "tree" && (
        <div className="max-h-[400px] overflow-y-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1 mb-2 text-[10px] font-mono">
            <button onClick={() => setTreePath("")} className="text-[var(--bw-accent)] hover:underline">root</button>
            {breadcrumbs.map((bc) => (
              <span key={bc.path} className="flex items-center gap-1">
                <ChevronRight size={10} className="text-[var(--bw-muted)]" />
                <button onClick={() => setTreePath(bc.path)} className="text-[var(--bw-accent)] hover:underline">{bc.label}</button>
              </span>
            ))}
          </div>

          {/* Parent dir */}
          {treePath && (
            <button
              onClick={() => setTreePath(pathParts.slice(0, -1).join("/"))}
              className="w-full flex items-center gap-2 px-2 py-1 text-xs text-[var(--bw-muted)] hover:text-[var(--bw-fg)] hover:bg-[var(--bw-surface-2)]"
            >
              <Folder size={14} /> ..
            </button>
          )}

          {treeEntries
            .sort((a, b) => {
              if (a.type !== b.type) return a.type === "tree" ? -1 : 1;
              return a.name.localeCompare(b.name);
            })
            .map((entry) => (
              <button
                key={entry.hash + entry.name}
                onClick={() => entry.type === "tree" ? setTreePath(treePath ? `${treePath}/${entry.name}` : entry.name) : undefined}
                className={`w-full flex items-center gap-2 px-2 py-1 text-xs transition-colors ${
                  entry.type === "tree"
                    ? "text-[var(--bw-fg)] hover:bg-[var(--bw-surface-2)] cursor-pointer"
                    : "text-[var(--bw-muted)] cursor-default"
                }`}
              >
                {entry.type === "tree"
                  ? <Folder size={14} className="text-[var(--bw-warn)] flex-shrink-0" />
                  : <FileCode size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
                }
                <span className="flex-1 text-left truncate">{entry.name}</span>
                <code className="text-[10px] text-[var(--bw-muted)]">{entry.hash}</code>
              </button>
            ))}
          {treeEntries.length === 0 && <p className="text-xs text-[var(--bw-muted)] py-4 text-center">Directorio vacío</p>}
        </div>
      )}
    </CorpoCard>
  );
}

// ============================================================================
// Project Card (compact list item)
// ============================================================================

function ProjectCard({ project, onSelect, selected }: { project: Project; onSelect: () => void; selected: boolean }) {
  const queryClient = useQueryClient();

  const cloneMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/projects/clone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: project.name }),
      });
      const data = await res.json() as { ok: boolean; error?: string };
      if (!data.ok) throw new Error(data.error ?? "clone failed");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  const isBare = project.kind === "bare";

  return (
    <div
      onClick={onSelect}
      className={`
        p-3 border cursor-pointer transition-all duration-150
        ${selected
          ? "border-[var(--bw-accent)] bg-[var(--bw-accent)]/5 shadow-[var(--bw-glow)]"
          : "border-[var(--bw-border)] bg-[var(--bw-surface)] hover:border-[var(--bw-accent)]"
        }
      `}
      style={{ clipPath: "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))" }}
    >
      <div className="flex items-center gap-2 mb-1">
        {isBare
          ? <Archive size={14} className="text-[var(--bw-muted)] flex-shrink-0" />
          : <FolderGit2 size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
        }
        <span className="text-sm font-bold uppercase text-[var(--bw-fg)] truncate flex-1">{project.name}</span>
        <StatusDot status={statusVariant(project.status)} size="sm" />
      </div>

      <div className="flex items-center gap-2">
        <HexBadge variant={isBare ? "muted" : "primary"} size="sm">{project.branch}</HexBadge>
        <HexBadge variant="muted" size="sm">{isBare ? "bare" : "worktree"}</HexBadge>
      </div>

      {project.lastCommit && (
        <p className="text-[10px] text-[var(--bw-muted)] mt-1 truncate font-mono">
          {project.lastCommit.hash} — {project.lastCommit.message}
        </p>
      )}

      {/* Clone button for bare repos */}
      {isBare && !project.hasWorktree && (
        <GlyphButton
          variant="secondary" size="sm" className="mt-2 w-full"
          icon={cloneMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          onClick={(e) => { e.stopPropagation(); cloneMutation.mutate(); }}
          disabled={cloneMutation.isPending}
        >
          {cloneMutation.isPending ? "Clonando..." : "Clonar a worktree"}
        </GlyphButton>
      )}
    </div>
  );
}

// ============================================================================
// Main Route
// ============================================================================

export function ProjectsRoute() {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "worktree" | "bare">("all");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["projects"],
    queryFn: () => fetch("/api/projects").then((r) => r.json()) as Promise<{ projects: Project[] }>,
  });

  const projects = data?.projects ?? [];
  const filtered = useMemo(() => {
    if (filter === "all") return projects;
    return projects.filter((p) => p.kind === filter);
  }, [projects, filter]);

  const selected = projects.find((p) => p.name === selectedProject) ?? null;
  const worktreeCount = projects.filter((p) => p.kind === "worktree").length;
  const bareCount = projects.filter((p) => p.kind === "bare").length;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <CRTHeader text="Git Dashboard" level={2} />
          <p className="text-xs text-[var(--bw-muted)] font-mono mt-1">
            {worktreeCount} worktree · {bareCount} bare
          </p>
        </div>
        <div className="flex gap-2">
          {(["all", "worktree", "bare"] as const).map((f) => (
            <GlyphButton key={f} variant={filter === f ? "primary" : "ghost"} size="sm" onClick={() => setFilter(f)}>
              {f === "all" ? "Todos" : f === "worktree" ? "Worktree" : "Bare"}
            </GlyphButton>
          ))}
          <GlyphButton variant="secondary" size="sm" icon={<RefreshCw size={12} />} onClick={() => refetch()}>
            Refresh
          </GlyphButton>
        </div>
      </div>

      {isLoading && <p className="text-[var(--bw-muted)] animate-pulse">Escaneando repos...</p>}

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Project list */}
        <div className="lg:w-80 space-y-2 flex-shrink-0">
          {filtered.map((p) => (
            <ProjectCard
              key={p.name}
              project={p}
              selected={selectedProject === p.name}
              onSelect={() => setSelectedProject(selectedProject === p.name ? null : p.name)}
            />
          ))}
          {!isLoading && filtered.length === 0 && (
            <p className="text-sm text-[var(--bw-muted)] text-center py-8">Sin repos</p>
          )}
        </div>

        {/* Detail panel */}
        <div className="flex-1 min-w-0">
          {selected ? (
            <ProjectDetail project={selected} onClose={() => setSelectedProject(null)} />
          ) : (
            <CorpoCard>
              <div className="text-center py-12">
                <FolderGit2 size={48} className="mx-auto text-[var(--bw-muted)] mb-3" />
                <p className="text-sm text-[var(--bw-muted)]">Selecciona un repositorio</p>
                <p className="text-xs text-[var(--bw-muted)] mt-1">Branches, commits, file tree</p>
              </div>
            </CorpoCard>
          )}
        </div>
      </div>
    </div>
  );
}
